import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { getFormattedDateTime } from "@/utils";
import {
  Mail,
  Phone,
  ArrowLeft,
  MapPin,
  Building,
  FileText,
  Activity,
  Edit,
  Plus,
  Trash2,
  Globe,
  Link2Off,
} from "lucide-react";
import { toast } from "sonner";
import { DocumentApi } from "@/api/document.api";
import countries from "@/constants/array/country_city.json";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;

const RedemptionMerchantView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const merchantFromState = location.state;
  const [merchant, setMerchant] = useState(merchantFromState || null);
  const [contacts, setContacts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(!merchantFromState);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [isAddContactDialogOpen, setIsAddContactDialogOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    contactName: "",
    contactEmail: "",
    contactRole: "",
    contactPhoneNumber: "",
  });
  const [isAddWarehouseDialogOpen, setIsAddWarehouseDialogOpen] =
    useState(false);
  const [warehouseForm, setWarehouseForm] = useState({
    name: "",
    email: "",
    address: "",
    country: "",
    city: "",
  });
  const [selectedWarehouseCountry, setSelectedWarehouseCountry] =
    useState(null);

  console.log(contacts, "contacts---");
  const [shippingProfileAssignments, setShippingProfileAssignments] = useState({
    assignedShippingProfiles: [],
    unAssignedShippingProfiles: [],
  });
  const [
    isAssignShippingProfileDialogOpen,
    setIsAssignShippingProfileDialogOpen,
  ] = useState(false);
  const [selectedShippingProfiles, setSelectedShippingProfiles] = useState([]);
  const [shippingProfileSearch, setShippingProfileSearch] = useState("");
  const [deleteWarehouseId, setDeleteWarehouseId] = useState(null);
  const [isDeleteWarehouseDialogOpen, setIsDeleteWarehouseDialogOpen] =
    useState(false);

  const [unassignShippingDialog, setUnassignShippingDialog] = useState(false);

  console.log(selectedShippingProfiles, "selectedShippingProfiles------");
  console.log(merchant, "merchant------");

  // Fetch merchant details only if not present in state

  useEffect(() => {
    if (!merchantFromState && id) {
      async function fetchMerchant() {
        setLoading(true);
        try {
          const res = await fetch(
            `${API_BASE}/redemptionMerchant/getRedemptionMerchants`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                pageNumber: 0,
                pageSize: 1,
                id: Number(id),
              }),
            }
          );
          const data = await res.json();
          setMerchant(data.redemptionMerchantVOS?.[0] || null);
        } catch (err) {
          toast.error("Failed to fetch merchant details");
        } finally {
          setLoading(false);
        }
      }
      fetchMerchant();
    }
    // Fetch shipping profile assignments
    async function fetchShippingProfileAssignments() {
      try {
        const res = await fetch(
          `${API_BASE}/shippingProfile/getShippingProfileAssignment`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ redemptionMerchantId: Number(id) }),
          }
        );
        const data = await res.json();
        setShippingProfileAssignments({
          assignedShippingProfiles: data.assignedShippingProfiles || [],
          unAssignedShippingProfiles: data.unAssignedShippingProfiles || [],
        });
      } catch (err) {
        toast.error("Failed to fetch shipping profile assignments");
      }
    }
    if (id) fetchShippingProfileAssignments();
  }, [id, merchantFromState]);

  // Fetch contacts
  useEffect(() => {
    async function fetchContacts() {
      try {
        const res = await fetch(
          `${API_BASE}/redemptionMerchant/getRedemptionMerchantContacts?redemptionMerchantId=${id}`,
          {
            credentials: "include",
          }
        );
        const data = await res.json();
        setContacts(data || []);
      } catch (err) {
        toast.error("Failed to fetch contacts");
        setContacts([]); // Set to empty array on error
      }
    }
    if (id) fetchContacts();
  }, [id]);

  // Fetch warehouses (shipping profiles)
  useEffect(() => {
    async function fetchWarehouses() {
      try {
        const res = await fetch(
          `${API_BASE}/redemptionMerchant/getRedemptionMerchantWarehouses?redemptionMerchantId=${id}`,
          {
            credentials: "include",
          }
        );
        const data = await res.json();
        setWarehouses(data || []);
      } catch (err) {
        toast.error("Failed to fetch shipping profiles");
        setWarehouses([]); // Set to empty array on error
      }
    }
    if (id) fetchWarehouses();
  }, [id]);

  // Fetch activity logs for merchant
  useEffect(() => {
    async function fetchActivity() {
      setActivityLoading(true);
      setActivityError(null);
      try {
        if (!merchant?.merchantCode) return;
        const payload = {
          id: null,
          programCode: null,
          merchantCode: merchant.merchantCode,
          pageNumber: 0,
          pageSize: 50,
          sortOrder: "desc",
          sortBy: "createdAt",
        };
        const res = await fetch(`${API_BASE}/activity/getActivityLogs`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        setActivityLogs(data.activityLogVOs || []);
      } catch (err) {
        setActivityError("Failed to fetch activity logs");
      } finally {
        setActivityLoading(false);
      }
    }
    if (merchant?.merchantCode) fetchActivity();
  }, [merchant?.merchantCode]);

  const handleDeleteWarehouse = async (warehouseId) => {
    try {
      const res = await fetch(
        `${API_BASE}/redemptionMerchant/deleteRedemptionMerchantWarehouse?redemptionMerchantWarehouseId=${warehouseId}`,
        {
          method: "GET",
          credentials: "include",
        }
      );
      if (!res.ok) throw new Error("Failed to delete warehouse");
      toast.success("Warehouse deleted successfully");
      // Refresh warehouses
      const fetchWarehouses = async () => {
        try {
          const res = await fetch(
            `${API_BASE}/redemptionMerchant/getRedemptionMerchantWarehouses?redemptionMerchantId=${id}`,
            {
              credentials: "include",
            }
          );
          const data = await res.json();
          setWarehouses(data || []);
        } catch (err) {
          toast.error("Failed to fetch shipping profiles");
          setWarehouses([]);
        }
      };
      fetchWarehouses();
    } catch (err) {
      toast.error("Failed to delete warehouse");
    } finally {
      setIsDeleteWarehouseDialogOpen(false);
      setDeleteWarehouseId(null);
    }
  };

  const handleAddWarehouse = async () => {
    try {
      if (
        !warehouseForm.name ||
        !warehouseForm.email ||
        !warehouseForm.address ||
        !warehouseForm.city ||
        !selectedWarehouseCountry
      ) {
        toast.error("Please fill all required fields.");
        return;
      }
      // Prepare new warehouse object
      const newWarehouse = {
        name: warehouseForm.name,
        address: warehouseForm.address,
        email: warehouseForm.email,
        city: warehouseForm.city,
        country: selectedWarehouseCountry?.name || "",
        countryVO: selectedWarehouseCountry
          ? {
              code: selectedWarehouseCountry.iso3,
              name: selectedWarehouseCountry.name,
            }
          : {},
        redemptionMerchantId: Number(id),
        isPrimary: warehouses.length === 0,
      };
      // Prepare payload: all existing warehouses (as is) + new warehouse
      const payload = [...warehouses, newWarehouse];
      const res = await fetch(
        `${API_BASE}/redemptionMerchant/createOrUpdateRedemptionMerchantWarehouse`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error("Failed to add warehouse");
      toast.success("Warehouse added successfully");
      // Refresh warehouses
      const fetchWarehouses = async () => {
        try {
          const res = await fetch(
            `${API_BASE}/redemptionMerchant/getRedemptionMerchantWarehouses?redemptionMerchantId=${id}`,
            {
              credentials: "include",
            }
          );
          const data = await res.json();
          setWarehouses(data || []);
        } catch (err) {
          toast.error("Failed to fetch shipping profiles");
          setWarehouses([]);
        }
      };
      fetchWarehouses();
      setIsAddWarehouseDialogOpen(false);
      setWarehouseForm({
        name: "",
        email: "",
        address: "",
        country: "",
        city: "",
      });
      setSelectedWarehouseCountry(null);
    } catch (err) {
      toast.error("Failed to add warehouse");
    }
  };

  async function fetchShippingProfileAssignments() {
    try {
      const res = await fetch(
        `${API_BASE}/shippingProfile/getShippingProfileAssignment`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ redemptionMerchantId: Number(id) }),
        }
      );
      const data = await res.json();
      setShippingProfileAssignments({
        assignedShippingProfiles: data.assignedShippingProfiles || [],
        unAssignedShippingProfiles: data.unAssignedShippingProfiles || [],
      });
    } catch (err) {
      toast.error("Failed to fetch shipping profile assignments");
    }
  }

  const handleUnlinkShippingProfile = async (profile: any) => {
    console.log(profile, "profile===");

    try {
      const res = await fetch(
        `${API_BASE}/shippingProfile/unAssignShippingProfileToRedemptionMerchant`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify([
            profile?.shippingProfileRedemptionMerchantIdForSelectedRedemptionMerchant,
          ]),
        }
      );
      if (!res.ok) throw new Error("Failed to assign shipping profile");
      toast.success("Unassigned successfully");
      setUnassignShippingDialog(false);
      if (id) fetchShippingProfileAssignments();
    } catch (err) {
      toast.error("Failed to unassign shipping profile");
    }
  };

  const handleShippingProfileAssignment = async () => {
    try {
      if (!merchant && selectedShippingProfiles.length === 0) {
        toast.error("Please select a shipping profile");
        return;
      }
      // Prepare new contact object
      const payload = {
        isActive: true,
        redemptionMerchantId: Number(merchant?.id),
        shippingProfileId: Number(selectedShippingProfiles[0]),
      };
      // Prepare payload: all existing contacts (as is) + new contact

      const res = await fetch(
        `${API_BASE}/shippingProfile/assignShippingProfileToRedemptionMerchant`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error("Failed to assign shipping profile");
      toast.success("Assigned successfully");

      if (id) fetchShippingProfileAssignments();

      setSelectedShippingProfiles([]);
      setIsAssignShippingProfileDialogOpen(false);
    } catch (err) {
      toast.error("Failed to assign shipping profile");
    }
  };

  const handleAddContact = async () => {
    try {
      if (
        !contactForm.contactName ||
        !contactForm.contactEmail ||
        !contactForm.contactRole ||
        !contactForm.contactPhoneNumber
      ) {
        toast.error("Please fill all required fields.");
        return;
      }
      // Prepare new contact object
      const newContact = {
        contactName: contactForm.contactName,
        contactEmail: contactForm.contactEmail,
        contactRole: contactForm.contactRole,
        contactPhoneNumber: contactForm.contactPhoneNumber,
        redemptionMerchantId: Number(id),
      };
      // Prepare payload: all existing contacts (as is) + new contact
      const payload = [...contacts, newContact];
      const res = await fetch(
        `${API_BASE}/redemptionMerchant/createOrUpdateRedemptionMerchantContact`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error("Failed to add contact");
      toast.success("Contact added successfully");
      // Refresh contacts
      const fetchContacts = async () => {
        try {
          const res = await fetch(
            `${API_BASE}/redemptionMerchant/getRedemptionMerchantContacts?redemptionMerchantId=${id}`,
            {
              credentials: "include",
            }
          );
          const data = await res.json();
          setContacts(data || []);
        } catch (err) {
          toast.error("Failed to fetch contacts");
          setContacts([]);
        }
      };
      fetchContacts();
      setIsAddContactDialogOpen(false);
      setContactForm({
        contactName: "",
        contactEmail: "",
        contactRole: "",
        contactPhoneNumber: "",
      });
    } catch (err) {
      toast.error("Failed to add contact");
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;
  if (!merchant) return <div className="p-8">Merchant not found.</div>;

  return (
    <div className="space-y-6 p-6 w-full">
      {/* Main heading with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Redemption Merchant Detail
          </h1>
          <p className="text-gray-500">View and manage merchant information</p>
        </div>
      </div>

      {/* Summary card */}
      <Card className="border-0 shadow-sm overflow-hidden bg-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {merchant.merchantName}
              </h2>
              <div className="flex items-center gap-3 mt-1 text-gray-500">
                <span className="text-sm">Code: {merchant.merchantCode}</span>
                <span className="text-sm">
                  Created:{" "}
                  {merchant.createdDate
                    ? getFormattedDateTime(merchant.createdDate) || "-"
                    : "-"}
                </span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4 mr-1" /> Edit
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="details" className="w-full">
        <TabsList className="bg-white border-b border-gray-200 w-full justify-start rounded-none h-12 px-6">
          <TabsTrigger
            value="details"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Redemption Merchant Details
          </TabsTrigger>
          <TabsTrigger
            value="contacts"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Contacts
          </TabsTrigger>
          <TabsTrigger
            value="warehouses"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Warehouses
          </TabsTrigger>
          <TabsTrigger
            value="shipping-profiles"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Shipping Profile
          </TabsTrigger>
        </TabsList>
        <TabsContent value="details" className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* General Information */}
            <Card className="border-0 shadow-sm bg-white h-full">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Merchant Name
                    </p>
                    <p className="text-gray-800">{merchant.merchantName}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Merchant Code
                    </p>
                    <p className="text-gray-800">{merchant.merchantCode}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Merchant Description
                    </p>
                    <p className="text-gray-800">
                      {merchant.merchantDescription || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Website</p>
                    <p className="text-blue-700 underline break-all">
                      {merchant.merchantWebsiteUrl || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Email</p>
                    <p className="text-gray-800">{merchant.merchantEmail}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Location */}
            <Card className="border-0 shadow-sm bg-white h-full">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Location
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Merchant Address
                    </p>
                    <p className="text-gray-800">
                      {merchant.merchantAddress || merchant.address || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Country</p>
                    <p className="text-gray-800">
                      {merchant.countryVO?.name || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">City</p>
                    <p className="text-gray-800">{merchant.city || "-"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Other */}
            <Card className="border-0 shadow-sm bg-white h-full">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Other
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Terms and Conditions
                    </p>
                    <p className="text-gray-800 whitespace-pre-line">
                      {merchant.termsAndConditionsTemplate || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      How to Use
                    </p>
                    <p className="text-gray-800 whitespace-pre-line">
                      {merchant.howToUseTemplate || "-"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="contacts" className="pt-6">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 bg-gray-50 p-6">
              <CardTitle className="text-lg font-medium text-gray-800">
                Contacts
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="flex items-center gap-2"
                  onClick={() => setIsAddContactDialogOpen(true)}
                >
                  <Plus className="h-4 w-4" /> Add Contact
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {!contacts || contacts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="mx-auto w-32 h-32 mb-4">
                    <img
                      src="/lovable-uploads/4c75fc6c-0171-49e7-9e79-ed3e2d9d4c44.png"
                      alt="No contacts"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-lg font-medium mb-1">No Contacts Found</p>
                  <p className="mb-4">
                    No contacts are currently added to this merchant.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    onClick={() => setIsAddContactDialogOpen(true)}
                  >
                    <Plus className="h-4 w-4" /> Create Your First Contact
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {contacts.map((contact, index) => (
                    <div
                      key={contact?.id || index}
                      className="border rounded-lg p-4 shadow-sm bg-white flex flex-col gap-2"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-lg text-gray-800">
                          {contact?.contactName}
                        </span>
                        <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                          {contact?.contactRole}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600 items-center">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" /> {contact?.contactEmail}
                        </span>
                        {contact?.contactPhoneNumber && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />{" "}
                            {contact?.contactPhoneNumber}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          {/* Add Contact Modal */}
          <Dialog
            open={isAddContactDialogOpen}
            onOpenChange={setIsAddContactDialogOpen}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Contact</DialogTitle>
              </DialogHeader>
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  <Input
                    placeholder="Enter contact name"
                    value={contactForm.contactName}
                    onChange={(e) =>
                      setContactForm((f) => ({
                        ...f,
                        contactName: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <Input
                    placeholder="Enter contact email"
                    value={contactForm.contactEmail}
                    onChange={(e) =>
                      setContactForm((f) => ({
                        ...f,
                        contactEmail: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <Input
                    placeholder="Enter contact role"
                    value={contactForm.contactRole}
                    onChange={(e) =>
                      setContactForm((f) => ({
                        ...f,
                        contactRole: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <Input
                    placeholder="Enter phone number"
                    value={contactForm.contactPhoneNumber}
                    onChange={(e) =>
                      setContactForm((f) => ({
                        ...f,
                        contactPhoneNumber: e.target.value,
                      }))
                    }
                  />
                </div>
                <DialogFooter className="sm:justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsAddContactDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="button" onClick={handleAddContact}>
                    <Plus className="h-4 w-4 mr-2" /> Add Contact
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>
        <TabsContent value="warehouses" className="pt-6">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 bg-gray-50 p-6">
              <CardTitle className="text-lg font-medium text-gray-800">
                Warehouses
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="flex items-center gap-2"
                  onClick={() => setIsAddWarehouseDialogOpen(true)}
                >
                  <Plus className="h-4 w-4" /> Add Warehouse
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {!warehouses || warehouses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="mx-auto w-32 h-32 mb-4">
                    <img
                      src="/lovable-uploads/4c75fc6c-0171-49e7-9e79-ed3e2d9d4c44.png"
                      alt="No warehouses"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-lg font-medium mb-1">
                    No Warehouses Found
                  </p>
                  <p className="mb-4">
                    No warehouses are currently added to this merchant.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    onClick={() => setIsAddWarehouseDialogOpen(true)}
                  >
                    <Plus className="h-4 w-4" /> Create Your First Warehouse
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {warehouses.map((wh) => (
                    <div
                      key={wh.id}
                      className="border rounded-lg p-4 shadow-sm bg-white flex flex-col gap-2"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-lg text-gray-800">
                            {wh.name}
                          </span>
                          {wh.isPrimary && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                              Primary
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="text-red-500 hover:text-red-700"
                          title="Delete Warehouse"
                          onClick={() => {
                            setDeleteWarehouseId(wh.id);
                            setIsDeleteWarehouseDialogOpen(true);
                          }}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600 items-center">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" /> {wh.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" /> {wh.city}
                        </span>
                        <span className="flex items-center gap-1">
                          <Globe className="w-4 h-4" /> {wh.countryVO?.name}
                          {wh.countryVO?.code && (
                            <span className="ml-1 text-xs text-gray-400">
                              ({wh.countryVO.code})
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Building className="w-4 h-4" /> {wh.address}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          {/* Add Warehouse Modal */}
          <Dialog
            open={isAddWarehouseDialogOpen}
            onOpenChange={setIsAddWarehouseDialogOpen}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Warehouse</DialogTitle>
              </DialogHeader>
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Warehouse Name
                  </label>
                  <Input
                    placeholder="Enter warehouse name"
                    value={warehouseForm.name}
                    onChange={(e) =>
                      setWarehouseForm((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <Input
                    placeholder="Enter email"
                    value={warehouseForm.email}
                    onChange={(e) =>
                      setWarehouseForm((f) => ({ ...f, email: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <Input
                    placeholder="Enter address"
                    value={warehouseForm.address}
                    onChange={(e) =>
                      setWarehouseForm((f) => ({
                        ...f,
                        address: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <select
                    className="block w-full border rounded px-3 py-2 text-gray-700"
                    value={warehouseForm.country}
                    onChange={(e) => {
                      const selected = JSON.parse(e.target.value);
                      setSelectedWarehouseCountry(selected);
                      setWarehouseForm((f) => ({
                        ...f,
                        country: e.target.value,
                        city: "",
                      }));
                    }}
                  >
                    <option value="">Select Country</option>
                    {(countries as any)?.map((country) => (
                      <option
                        key={country?.name}
                        value={JSON.stringify(country)}
                      >
                        {country?.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <select
                    className="block w-full border rounded px-3 py-2 text-gray-700"
                    value={warehouseForm.city}
                    onChange={(e) =>
                      setWarehouseForm((f) => ({ ...f, city: e.target.value }))
                    }
                    disabled={!selectedWarehouseCountry?.cities?.length}
                  >
                    <option value="">Select City</option>
                    {selectedWarehouseCountry?.cities?.map((city) => (
                      <option key={city?.id} value={city?.name}>
                        {city?.name}
                      </option>
                    ))}
                  </select>
                </div>
                <DialogFooter className="sm:justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsAddWarehouseDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="button" onClick={handleAddWarehouse}>
                    <Plus className="h-4 w-4 mr-2" /> Add Warehouse
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>
        <TabsContent value="shipping-profiles" className="pt-6">
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="border-b border-gray-100 bg-gray-50 p-6 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-medium text-gray-800">
                Assigned Shipping Profiles
              </CardTitle>
              <Button
                size="sm"
                className="flex items-center gap-2"
                onClick={() => setIsAssignShippingProfileDialogOpen(true)}
              >
                <Plus className="h-4 w-4" /> Assign Shipping Profile
              </Button>
            </CardHeader>
            <CardContent className="p-6">
              {Array.isArray(
                shippingProfileAssignments?.assignedShippingProfiles
              ) &&
              shippingProfileAssignments.assignedShippingProfiles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {shippingProfileAssignments.assignedShippingProfiles.map(
                    (profile) => (
                      <div
                        key={profile.id}
                        className="border rounded-lg p-4 shadow-sm bg-gray-50 flex flex-col gap-2"
                      >
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-800 text-lg">
                            {profile.name}
                          </p>
                          {profile.isActiveForSelectedRedemptionMerchant && (
                            <span className="">
                              <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                Active
                                {/* <button
                                type="button"
                                className="text-red-500 hover:text-red-700"
                                title="Delete Warehouse"
                                onClick={() => {
                                  setDeleteWarehouseId(wh.id);
                                  setIsDeleteWarehouseDialogOpen(true);
                                }}
                              >
                                <Trash2 className="w-5 h-5" />
                              </button> */}
                              </span>
                              <span className="ml-4">
                                <button
                                  type="button"
                                  className="text-red-500 hover:text-red-700"
                                  title="Unassign shipping profile"
                                  onClick={() => {
                                    // handleUnlinkShippingProfile(profile);
                                    setUnassignShippingDialog(true);
                                  }}
                                >
                                  <Link2Off className="w-5 h-5" />
                                </button>{" "}
                                <AlertDialog
                                  open={unassignShippingDialog}
                                  // onOpenChange={() =>
                                  //   setUnassignShippingDialog(true)
                                  // }
                                >
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>
                                        Unassign Shipping Profile
                                      </AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Are you sure you want to Unassign this
                                        shipping profile?
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel
                                        onClick={() =>
                                          setUnassignShippingDialog(false)
                                        }
                                      >
                                        Cancel
                                      </AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => {
                                          handleUnlinkShippingProfile(profile);
                                        }}
                                        // onClick={handleConfirm}
                                        className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                                      >
                                        Confirm
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </span>
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {profile.description || "No description"}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2 items-center">
                          {profile.countries?.slice(0, 3).map((country) => (
                            <span
                              key={country.id}
                              className="bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 text-xs font-medium rounded"
                            >
                              {country.name}
                            </span>
                          ))}
                          {profile.countries &&
                            profile.countries.length > 3 && (
                              <span className="bg-gray-200 text-gray-700 px-2 py-0.5 text-xs font-medium rounded">
                                ...
                              </span>
                            )}
                        </div>
                      </div>
                    )
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="mx-auto w-32 h-32 mb-4">
                    <img
                      src="/lovable-uploads/4c75fc6c-0171-49e7-9e79-ed3e2d9d4c44.png"
                      alt="No shipping profiles"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-lg font-medium mb-1">
                    No Shipping Profiles Assigned
                  </p>
                  <p className="mb-4">
                    No shipping profiles are currently assigned to this
                    merchant.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
          {/* Assign Shipping Profile Modal */}
          <Dialog
            open={isAssignShippingProfileDialogOpen}
            onOpenChange={setIsAssignShippingProfileDialogOpen}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Assign Shipping Profile</DialogTitle>
                <p className="text-gray-500 text-sm mt-1">
                  Select shipping profiles to assign to this merchant.
                </p>
              </DialogHeader>
              <div className="my-4">
                <Input
                  placeholder="Search shipping profiles..."
                  value={shippingProfileSearch}
                  onChange={(e) => setShippingProfileSearch(e.target.value)}
                  className="mb-3"
                />
                <div className="flex flex-wrap gap-1 mb-2">
                  {selectedShippingProfiles.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No shipping profiles selected
                    </p>
                  ) : (
                    selectedShippingProfiles.map((profileId) => {
                      const profile =
                        shippingProfileAssignments?.unAssignedShippingProfiles?.find(
                          (p) => p.id === profileId
                        );
                      return profile ? (
                        <span
                          key={profileId}
                          className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1"
                        >
                          {profile.name}
                          <button
                            onClick={() =>
                              setSelectedShippingProfiles(
                                selectedShippingProfiles.filter(
                                  (id) => id !== profileId
                                )
                              )
                            }
                            className="ml-1 h-3 w-3 rounded-full bg-gray-400 text-white flex items-center justify-center"
                          >
                            ×
                          </button>
                        </span>
                      ) : null;
                    })
                  )}
                </div>
                <div className="max-h-60 overflow-y-auto border rounded p-2 bg-gray-50">
                  {shippingProfileAssignments?.unAssignedShippingProfiles
                    ?.filter((profile) =>
                      profile.name
                        .toLowerCase()
                        .includes(shippingProfileSearch.toLowerCase())
                    )
                    .map((profile) => (
                      <div
                        key={profile.id}
                        className={`flex items-center justify-between px-2 py-2 rounded cursor-pointer hover:bg-blue-100 ${
                          selectedShippingProfiles.includes(profile.id)
                            ? "bg-blue-100"
                            : ""
                        }`}
                        onClick={() => {
                          setSelectedShippingProfiles((sel) =>
                            sel.includes(profile.id)
                              ? sel.filter((id) => id !== profile.id)
                              : [...sel, profile.id]
                          );
                        }}
                      >
                        <div>
                          <p className="font-medium text-gray-800">
                            {profile.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {profile.description || "No description"}
                          </p>
                        </div>
                        {selectedShippingProfiles.includes(profile.id) && (
                          <span className="text-blue-600 font-bold ml-2">
                            ✓
                          </span>
                        )}
                      </div>
                    ))}
                  {(!shippingProfileAssignments?.unAssignedShippingProfiles ||
                    shippingProfileAssignments.unAssignedShippingProfiles
                      .length === 0) && (
                    <div className="text-center text-gray-400 py-4">
                      No unassigned shipping profiles found.
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsAssignShippingProfileDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    handleShippingProfileAssignment();
                    // setIsAssignShippingProfileDialogOpen(false);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" /> Assign Selected
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
      <Dialog
        open={isDeleteWarehouseDialogOpen}
        onOpenChange={setIsDeleteWarehouseDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Warehouse</DialogTitle>
          </DialogHeader>
          <div className="mb-4">
            Are you sure you want to delete this warehouse?
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteWarehouseDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDeleteWarehouse(deleteWarehouseId)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RedemptionMerchantView;
